import React, { useEffect, useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Settings as SettingsIcon, ShieldAlert, Activity, RefreshCw } from "lucide-react";
import PeerSyncPanel from "@/components/PeerSyncPanel";

export default function Settings() {
    const { activeTenant } = useTenant();
    const { user } = useAuth();
    const [staffel, setStaffel] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [activity, setActivity] = useState([]);

    const isAdmin = user?.role === "admin";

    const load = async () => {
        if (!activeTenant) return;
        try {
            const [wb, ts, ac] = await Promise.all([
                api.get("/wb-config", { params: { tenant_id: activeTenant.tenant_id } }),
                api.get("/tenants"),
                api.get("/dashboard/aktivitaet", { params: { tenant_id: activeTenant.tenant_id } }),
            ]);
            setStaffel(wb.data.staffel || []);
            setTenants(ts.data);
            setActivity(ac.data || []);
        } catch {
            toast.error("Einstellungen konnten nicht geladen werden");
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTenant?.tenant_id]);

    const updateStaffel = (idx, value) => {
        const next = [...staffel];
        next[idx] = { ...next[idx], prozent: parseFloat(value) || 0 };
        setStaffel(next);
    };

    const saveStaffel = async () => {
        try {
            await api.put("/wb-config", { tenant_id: activeTenant.tenant_id, staffel });
            toast.success("WB-Staffel gespeichert");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
        }
    };

    if (!isAdmin) {
        return (
            <div className="max-w-2xl mx-auto mt-20 text-center animate-fade-up">
                <ShieldAlert className="h-12 w-12 mx-auto text-yellow-400" />
                <h2 className="font-heading text-2xl mt-4">Keine Berechtigung</h2>
                <p className="text-muted-foreground mt-2">
                    Einstellungen sind ausschließlich für Administratoren zugänglich.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-12 animate-fade-up">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Einstellungen</div>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">Konfiguration</h1>
            </div>

            <Tabs defaultValue="wb" className="w-full">
                <TabsList data-testid="settings-tabs">
                    <TabsTrigger value="wb">WB-Staffel</TabsTrigger>
                    <TabsTrigger value="verlage">Verlage</TabsTrigger>
                    <TabsTrigger value="aktivitaeten" data-testid="tab-aktivitaeten">Aktivitäten</TabsTrigger>
                    <TabsTrigger value="peer-sync" data-testid="tab-peer-sync">
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Peer-Sync
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="wb" className="mt-6">
                    <Card className="p-6 bg-surface1 border-border max-w-2xl">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            <SettingsIcon className="h-3.5 w-3.5" /> Wertberichtigungs-Staffel · {activeTenant?.name}
                        </div>
                        <h3 className="font-heading text-xl font-semibold mt-1">WB-% je Buchalter</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Definieren Sie den Wertberichtigungs-Prozentsatz je Alter des Titels (1. Jahr = Erscheinungsjahr).
                        </p>
                        <div className="mt-4 space-y-3">
                            {staffel.map((s, idx) => (
                                <div key={s.jahr_alter} className="flex items-center gap-3">
                                    <Label className="w-36">
                                        {s.jahr_alter === 5 ? "5. Jahr & älter" : `${s.jahr_alter}. Jahr`}
                                    </Label>
                                    <Input
                                        type="number"
                                        value={s.prozent}
                                        onChange={(e) => updateStaffel(idx, e.target.value)}
                                        className="max-w-[140px]"
                                        data-testid={`wb-input-${s.jahr_alter}`}
                                    />
                                    <span className="text-sm text-muted-foreground">%</span>
                                </div>
                            ))}
                        </div>
                        <Button onClick={saveStaffel} className="mt-5 bg-tenant text-[color:var(--tenant-accent-foreground)]" data-testid="save-wb-btn">
                            Speichern
                        </Button>
                    </Card>
                </TabsContent>

                <TabsContent value="verlage" className="mt-6">
                    <div className="grid md:grid-cols-3 gap-5">
                        {tenants.map((t) => (
                            <Card key={t.tenant_id} className="p-5 bg-surface1 border-border">
                                <div className="flex items-center gap-3">
                                    <span className="h-10 w-1 rounded-full" style={{ background: t.color }} />
                                    <div>
                                        <div className="font-heading text-lg font-semibold">{t.name}</div>
                                        <div className="text-sm text-muted-foreground">{t.city}</div>
                                    </div>
                                </div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-4">
                                    Akzentfarbe
                                </div>
                                <div className="font-mono text-sm mt-1">{t.color}</div>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="aktivitaeten" className="mt-6">
                    <Card className="p-6 bg-surface1 border-border max-w-3xl" data-testid="aktivitaeten-card">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                            <Activity className="h-3.5 w-3.5" /> Letzte Aktivitäten · {activeTenant?.name}
                        </div>
                        <h3 className="font-heading text-xl font-semibold mt-1">
                            Audit-Trail (letzte 20)
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Importe, Resets und Konfigurationsänderungen pro Mandant.
                        </p>
                        <ul className="mt-5 space-y-4 max-h-[480px] overflow-auto pr-2">
                            {activity.length === 0 && (
                                <li className="text-sm text-muted-foreground">Noch keine Aktivitäten.</li>
                            )}
                            {activity.map((a, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="h-1.5 w-1.5 mt-2 rounded-full bg-tenant flex-shrink-0" />
                                    <div className="text-sm min-w-0">
                                        <div className="break-words">{a.message}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                                            {a.user_name} · {new Date(a.ts).toLocaleString("de-DE")}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </Card>
                </TabsContent>

                <TabsContent value="peer-sync" className="mt-6">
                    <PeerSyncPanel />
                </TabsContent>
            </Tabs>
        </div>
    );
}